import { useNavigate } from "react-router-dom";
import { AlertBanner } from "@/components/admin/alert-banner";
import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { Kpi } from "@/components/admin/kpi";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { useFinanceDashboard } from "@/lib/finance/finance-hooks";
import { fmtFCFA } from "@/lib/format";

/** FIN-01 — Tableau de bord trésorerie consolidée. */
export function AdminFinancePage() {
	const navigate = useNavigate();
	const { data, isLoading } = useFinanceDashboard();

	return (
		<>
			<PageHead
				title="Finance & comptabilité"
				sub="Trésorerie consolidée temps réel · banques, Mobile Money, caisses hubs"
				actions={
					<>
						<AdminButton
							variant="ghost"
							onClick={() => navigate("/finance/exports")}
						>
							<AdminIcon n="download" s={14} />
							Exports SYSCOHADA
						</AdminButton>
						<AdminButton
							variant="primary"
							onClick={() => navigate("/finance/factures/nouvelle")}
						>
							<AdminIcon n="plus" s={14} />
							Nouvelle facture
						</AdminButton>
					</>
				}
			/>

			{(data?.alerts ?? []).map((a, i) => (
				<AlertBanner
					key={i}
					variant={
						a.level === "danger"
							? "danger"
							: a.level === "warn"
								? "warning"
								: "info"
					}
					title={a.message}
				/>
			))}

			<div className="kpi-grid">
				<Kpi
					label="Trésorerie consolidée"
					value={isLoading ? "…" : fmtFCFA(data?.treasury ?? 0)}
					foot={`${data?.treasuryByAccount?.length ?? 0} comptes`}
				/>
				<Kpi
					label="Créances clients (411)"
					value={isLoading ? "…" : fmtFCFA(data?.receivables ?? 0)}
					variant="accent"
					foot="Solde débiteur clients"
				/>
				<Kpi
					label="Dettes fournisseurs (401)"
					value={isLoading ? "…" : fmtFCFA(data?.payables ?? 0)}
					variant="warning"
					foot="Solde créditeur fournisseurs"
				/>
				<Kpi
					label="TVA collectée (mois)"
					value={isLoading ? "…" : fmtFCFA(data?.vatCollectedMonth ?? 0)}
					variant="forest"
					foot="Compte 4431"
				/>
			</div>

			<div
				style={{
					display: "grid",
					gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
					gap: 16,
				}}
			>
				<Card>
					<CardHead
						title="Trésorerie par compte"
						sub="Classe 5 — soldes courants"
					/>
					<CardBody>
						<div className="table-wrap" style={{ margin: 0 }}>
							<table className="table">
								<thead>
									<tr>
										<th>Compte</th>
										<th>Libellé</th>
										<th className="right">Solde</th>
									</tr>
								</thead>
								<tbody>
									{(data?.treasuryByAccount ?? []).map((a) => (
										<tr key={a.accountCode}>
											<td className="mono">{a.accountCode}</td>
											<td>{a.label}</td>
											<td className="right tn">
												<strong>{fmtFCFA(a.balance)}</strong>
											</td>
										</tr>
									))}
									{!isLoading &&
										(data?.treasuryByAccount?.length ?? 0) === 0 && (
											<tr>
												<td
													colSpan={3}
													className="muted"
													style={{ textAlign: "center", padding: 20 }}
												>
													Aucun mouvement de trésorerie.
												</td>
											</tr>
										)}
								</tbody>
							</table>
						</div>
					</CardBody>
				</Card>

				<Card>
					<CardHead
						title="Recouvrement"
						sub="Factures échues non réglées"
						right={
							<AdminButton
								variant="ghost"
								size="sm"
								onClick={() => navigate("/finance/factures?status=OVERDUE")}
							>
								<AdminIcon n="eye" s={13} />
								Voir
							</AdminButton>
						}
					/>
					<CardBody>
						<div
							style={{
								display: "flex",
								gap: 24,
								alignItems: "baseline",
								flexWrap: "wrap",
							}}
						>
							<div>
								<div className="kpi-v tn" style={{ fontSize: 28 }}>
									{data?.overdueCount ?? 0}
								</div>
								<div className="muted">factures en retard</div>
							</div>
							<div>
								<div className="kpi-v tn" style={{ fontSize: 28 }}>
									{fmtFCFA(data?.overdueAmount ?? 0)}
								</div>
								<div className="muted">à recouvrer</div>
							</div>
						</div>
						<div
							style={{
								marginTop: 16,
								display: "flex",
								flexWrap: "wrap",
								gap: 8,
							}}
						>
							<Tag variant="info" noDot>
								<AdminIcon n="bank" s={12} /> SYSCOHADA OHADA
							</Tag>
							<Tag variant="neutral" noDot>
								Devise pivot : XAF
							</Tag>
							<Tag variant="neutral" noDot>
								TVA 19,25 %
							</Tag>
						</div>
					</CardBody>
				</Card>
			</div>

			<Card style={{ marginTop: 16 }}>
				<CardHead title="Accès rapides" sub="Modules FIN-02 … FIN-11" />
				<CardBody>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
							gap: 10,
						}}
					>
						{[
							{ l: "Factures", p: "/finance/factures", i: "card" as const },
							{ l: "Avoirs", p: "/finance/avoirs", i: "refresh" as const },
							{ l: "Journaux", p: "/finance/journaux", i: "layout" as const },
							{
								l: "Plan comptable",
								p: "/finance/plan-comptable",
								i: "chart" as const,
							},
							{ l: "Déclaration TVA", p: "/finance/tva", i: "flag" as const },
							{
								l: "États financiers",
								p: "/finance/etats",
								i: "chart" as const,
							},
							{ l: "Budgets", p: "/finance/budgets", i: "trend" as const },
							{
								l: "Audit & contrôles",
								p: "/finance/audit",
								i: "shield" as const,
							},
						].map((q) => (
							<button
								key={q.p}
								className="btn-ghost"
								style={{ justifyContent: "flex-start", gap: 8 }}
								onClick={() => navigate(q.p)}
							>
								<AdminIcon n={q.i} s={15} />
								{q.l}
							</button>
						))}
					</div>
				</CardBody>
			</Card>
		</>
	);
}
